/**
 * Helios Brain — visual library.
 *
 * Aesthetic: DARK CINEMATIC ABSTRACT
 *   - Earth-from-space, atmospheric ridges, dark fluid macros
 *   - Strictly black/dark gray dominant, with subtle warm/cool atmospheric light
 *   - NO pastel cream, NO Joel Filipe pastels, NO portraits
 *
 * One coherent visual family across every page.
 */

const U = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1800&q=85`;

// ─── Verified dark cinematic base assets ───
const VERIFIED = {
  // User-uploaded heroes (saved locally in /public)
  hero_earth:        "/HS-hero.webp",                 // older lifestyle Earth (kept as fallback)
  earth_half_lit:    "/earth-space.webp",             // user-uploaded: half-lit Earth from space (THE signature hero)
  chess_strategy:    "/chess-strategy.webp",          // user-uploaded: dark chess close-up — "rehearse the decision"
  iridescent_glass:  "/framework-spheres.webp",       // user-uploaded: iridescent glass spheres — for the Framework page (needs darker overlay)

  // ─── Greece (user-requested: 4 διαφορετικές ελληνικές, όχι μόνο Αθήνα) ───
  athens_night:       U("photo-1761700972079-42a1b4a79de2"),   // Acropolis illuminated at night, overlooking Athens
  athens_dusk:        U("photo-1761700970980-e3ab28ae95fb"),   // Acropolis overlooking Athens at dusk
  santorini_aegean:   U("photo-1743427495409-bf19fdaa4c16"),   // Santorini: blue-domed church overlooking the Aegean
  santorini_white:    U("photo-1569922732333-679cb0cb35a1"),   // Santorini: white dome buildings

  // Atmospheric mountain / aerial ridges (dark, dramatic)
  ridge_dark:        U("photo-1465056836041-7f43ac27dcb5"),   // dark peaks behind clouds
  forest_ridge:      U("photo-1419242902214-272b3f66ee7a"),   // Szmigiel dark forest layers
  atmospheric:       U("photo-1542736667-069246bdbc6d"),      // dark atmospheric clouds
  alps_dark:         U("photo-1454496522488-7a8e488e8606"),   // dark snow ridge
  aerial_storm:      U("photo-1500964757637-c85e8a162699"),   // dark aerial storm
  ocean_dark:        U("photo-1505142468610-359e7d316be0"),   // dark ocean abyss

  // Cosmic / planetary (dark space, subtle warm rim)
  earth_curve:       U("photo-1451187580459-43490279c0fa"),   // earth curvature from orbit
  cosmic_dust:       U("photo-1465101046530-73398c7f28ca"),   // dark cosmic atmosphere
  nebula_dark:       U("photo-1462331940025-496dfbfc7564"),   // deep nebula
  galaxy_core:       U("photo-1419242902214-272b3f66ee7a"),   // dark spiral suggestion

  // Abstract dark macros (fluid, texture, light through glass)
  fluid_dark:        U("photo-1532009324734-20a7a5813719"),   // dark abstract fluid
  prism_dark:        U("photo-1547036967-23d11aacaee0"),      // dark light prism
  architectural:     U("photo-1508739773434-c26b3d09e071"),   // dark architectural concrete
  topology_dark:     U("photo-1518709268805-4e9042af9f23"),   // dark abstract topology
  ink_macro:         U("photo-1517511620798-cec17d428bc0"),   // ink in water macro
  smoke_dark:        U("photo-1419242902214-272b3f66ee7a"),   // alt dark abstract
};

/**
 * Industry visual library — 15 industries × 2 photos each.
 *
 *   hero  = sticky hero on /industries/<slug>, bento card on /industries,
 *           and the Models in Production tile on the homepage.
 *   inner = FeaturedCallout image in the middle of the industry page.
 *
 * Aesthetic rule: every photo is industry-specific (a bank looks like banking,
 * a turbine looks like energy), dark-leaning, cinematic. Stock-art portraits
 * are NOT used — only the thing of the industry itself.
 */
const INDUSTRY_PHOTOS = {
  banking: {
    hero:  U("photo-1486406146926-c627a92ad1ab"),   // financial district at night
    inner: U("photo-1611974789855-9c2a0a7236a3"),   // candlestick chart on black
  },
  sovereign: {
    hero:  U("photo-1521791136064-7986c2920216"),   // institutional handshake / accord
    inner: U("photo-1517502884422-41eaead166d4"),   // empty institutional boardroom
  },
  real_estate: {
    hero:  U("photo-1486325212027-8081e485255e"),   // modern architecture
    inner: U("photo-1487958449943-2429e8be8625"),   // architectural detail
  },
  insurance: {
    hero:  U("photo-1450101499163-c8848c66ca85"),   // contracts / handshake
    inner: U("photo-1554224155-1696413565d3"),      // umbrellas / risk pool
  },
  healthcare: {
    hero:  U("photo-1538108149393-fbbd81895907"),   // hospital corridor
    inner: U("photo-1631815589968-fdb09a223b1e"),   // medical equipment close
  },
  energy: {
    hero:  U("photo-1466611653911-95081537e5b7"),   // wind turbines
    inner: U("photo-1497435334941-8c899ee9e8e9"),   // transmission lines
  },
  manufacturing: {
    hero:  U("photo-1565793298595-6a879b1d9492"),   // aerial trucks / supply chain
    inner: U("photo-1567789884554-0b844b597180"),   // car factory robot welders
  },
  public_sector: {
    hero:  U("photo-1529107386315-e1a2ed48a620"),   // parliament / capitol
    inner: U("photo-1611516491426-03025e6043c8"),   // aerial city + golden dome at twilight
  },
  telecom: {
    hero:  U("photo-1480694313141-fce5e697ee25"),   // cell tower at dusk
    inner: U("photo-1597733336794-12d05021d510"),   // fiber optic burst
  },
  retail: {
    hero:  U("photo-1481437156560-3205f6a55735"),   // retail / store
    inner: U("photo-1567401893414-76b7b1e5a7a5"),   // mall interior
  },
  maritime: {
    hero:  U("photo-1494412519320-aa613dfb7738"),   // aerial container yard
    inner: U("photo-1494412574643-ff11b0a5c1c3"),   // port terminal crane
  },
  defense: {
    hero:  U("photo-1583373834259-46cc92173cb7"),   // government complex at sunset
    inner: U("photo-1635070041078-e363dbe005cb"),   // mathematical strategy chalkboard
  },
  space: {
    hero:  U("photo-1614728263952-84ea256f9679"),   // satellite over earth
    inner: U("photo-1457364887197-9150188c107b"),   // cosmic / orbital
  },
  robotics: {
    hero:  U("photo-1535378620166-273708d44e4c"),   // service robot in dark room
    inner: U("photo-1531746790731-6c087fecd65a"),   // white robotic hand close
  },
  physical: {
    hero:  U("photo-1518770660439-4636190af475"),   // dark circuit board / embedded
    inner: U("photo-1581092918056-0c4c3acd3789"),   // sensor electronics
  },
};

// ─── Semantic mapping — every page key maps to a verified dark base ───
const M = {
  // Heroes
  smart_neural:       VERIFIED.hero_earth,          // Home hero
  smart_code:         VERIFIED.ridge_dark,          // Brain hero
  smart_lab:          VERIFIED.cosmic_dust,         // Factory hero
  smart_fluid:        VERIFIED.fluid_dark,          // Research hero
  smart_threads:      VERIFIED.forest_ridge,        // LLM hero, About hero
  smart_architecture: VERIFIED.architectural,       // Careers hero
  smart_fiber:        VERIFIED.topology_dark,       // Product hero, Factory wiring
  smart_paper:        VERIFIED.prism_dark,          // Insights billboard
  smart_circuit:      VERIFIED.atmospheric,
  smart_lab_alt:      VERIFIED.fluid_dark,

  // Industry visualisations — each UNIQUE, industry-specific photograph.
  banking:            INDUSTRY_PHOTOS.banking.hero,
  sovereign:          INDUSTRY_PHOTOS.sovereign.hero,
  real_estate:        INDUSTRY_PHOTOS.real_estate.hero,
  insurance:          INDUSTRY_PHOTOS.insurance.hero,
  healthcare:         INDUSTRY_PHOTOS.healthcare.hero,
  energy:             INDUSTRY_PHOTOS.energy.hero,
  manufacturing:      INDUSTRY_PHOTOS.manufacturing.hero,
  public_sector:      INDUSTRY_PHOTOS.public_sector.hero,
  telecom:            INDUSTRY_PHOTOS.telecom.hero,
  retail:             INDUSTRY_PHOTOS.retail.hero,
  maritime:           INDUSTRY_PHOTOS.maritime.hero,
  defense:            INDUSTRY_PHOTOS.defense.hero,
  space:              INDUSTRY_PHOTOS.space.hero,
  robotics:           INDUSTRY_PHOTOS.robotics.hero,
  physical:           INDUSTRY_PHOTOS.physical.hero,

  // Editorial / generic
  analyst:            VERIFIED.architectural,
  researcher:         VERIFIED.fluid_dark,
  founder_portrait:   VERIFIED.forest_ridge,
};

export const HELIOS_IMG = {
  ...VERIFIED,
  ...M,

  // ─── Slot keys consumed by hero/section components.
  //     These start with VERIFIED defaults but can be overwritten at
  //     runtime via hydrateImageOverrides() once /api/photos/active loads.
  //     EVERY HERO IS UNIQUE — no two pages share the same image.
  home_hero:         VERIFIED.earth_half_lit,        // user-requested: half-lit Earth from space
  product_hero:      VERIFIED.earth_curve,           // Earth at night from orbit (different angle from home)
  helios_hero:       VERIFIED.topology_dark,          // dark fortress in fog (private model · institutional)
  brain_hero:        VERIFIED.chess_strategy,        // dark chess close-up — "rehearse the decision before you make it"
  factory_hero:      VERIFIED.cosmic_dust,           // dark cosmic atmosphere
  research_hero:     VERIFIED.fluid_dark,            // dark fluid macro
  framework_hero:    VERIFIED.iridescent_glass,      // user-requested: iridescent glass spheres (heavily darkened on page)
  industries_hero:   VERIFIED.ridge_dark,            // dark peaks behind clouds
  company_hero:      VERIFIED.architectural,          // dark architectural concrete (neutral, international)
  careers_hero:      VERIFIED.alps_dark,              // dark alps ridge (neutral, international)
  greek_signature:   VERIFIED.athens_dusk,            // ΜΟΝΟ ένα Greek photo — Acropolis at dusk (μέσα στο "From Athens" chapter)
  // ─── Per-industry HERO slots (sticky hero, bento card, homepage tile) ───
  industry_banking:        INDUSTRY_PHOTOS.banking.hero,
  industry_sovereign:      INDUSTRY_PHOTOS.sovereign.hero,
  industry_real_estate:    INDUSTRY_PHOTOS.real_estate.hero,
  industry_insurance:      INDUSTRY_PHOTOS.insurance.hero,
  industry_healthcare:     INDUSTRY_PHOTOS.healthcare.hero,
  industry_energy:         INDUSTRY_PHOTOS.energy.hero,
  industry_manufacturing:  INDUSTRY_PHOTOS.manufacturing.hero,
  industry_public_sector:  INDUSTRY_PHOTOS.public_sector.hero,
  industry_telecom:        INDUSTRY_PHOTOS.telecom.hero,
  industry_retail:         INDUSTRY_PHOTOS.retail.hero,
  industry_maritime:       INDUSTRY_PHOTOS.maritime.hero,
  industry_defense:        INDUSTRY_PHOTOS.defense.hero,
  industry_space:          INDUSTRY_PHOTOS.space.hero,
  industry_robotics:       INDUSTRY_PHOTOS.robotics.hero,
  industry_physical:       INDUSTRY_PHOTOS.physical.hero,
  // ─── Per-industry INNER slots (FeaturedCallout inside each detail page) ───
  industry_banking_inner:        INDUSTRY_PHOTOS.banking.inner,
  industry_sovereign_inner:      INDUSTRY_PHOTOS.sovereign.inner,
  industry_real_estate_inner:    INDUSTRY_PHOTOS.real_estate.inner,
  industry_insurance_inner:      INDUSTRY_PHOTOS.insurance.inner,
  industry_healthcare_inner:     INDUSTRY_PHOTOS.healthcare.inner,
  industry_energy_inner:         INDUSTRY_PHOTOS.energy.inner,
  industry_manufacturing_inner:  INDUSTRY_PHOTOS.manufacturing.inner,
  industry_public_sector_inner:  INDUSTRY_PHOTOS.public_sector.inner,
  industry_telecom_inner:        INDUSTRY_PHOTOS.telecom.inner,
  industry_retail_inner:         INDUSTRY_PHOTOS.retail.inner,
  industry_maritime_inner:       INDUSTRY_PHOTOS.maritime.inner,
  industry_defense_inner:        INDUSTRY_PHOTOS.defense.inner,
  industry_space_inner:          INDUSTRY_PHOTOS.space.inner,
  industry_robotics_inner:       INDUSTRY_PHOTOS.robotics.inner,
  industry_physical_inner:       INDUSTRY_PHOTOS.physical.inner,

  // ─── Legacy aliases (all collapse to dark cinematic bases) ───
  thoughtful_man:     VERIFIED.forest_ridge,
  studio_close:       VERIFIED.architectural,
  side_light:         VERIFIED.prism_dark,
  shadow_face:        VERIFIED.architectural,
  hands_workspace:    VERIFIED.architectural,
  exec_window:        VERIFIED.ridge_dark,
  boardroom_warm:     VERIFIED.architectural,
  expert_focus:       VERIFIED.fluid_dark,
  team_room:          VERIFIED.fluid_dark,
  woman_focused:      VERIFIED.forest_ridge,
  man_office:         VERIFIED.architectural,
  woman_window:       VERIFIED.prism_dark,
  hands_strategy:     VERIFIED.architectural,
  analyst_data:       VERIFIED.cosmic_dust,
  founder_warm:       VERIFIED.forest_ridge,
  researcher_close:   VERIFIED.fluid_dark,

  // Landscape aliases
  mountain_sunset:    VERIFIED.ridge_dark,
  twilight_ridge:     VERIFIED.ridge_dark,
  fog_peaks:          VERIFIED.atmospheric,
  alps_blue:          VERIFIED.alps_dark,
  aerial_coast:       VERIFIED.forest_ridge,
  aerial_city_night:  VERIFIED.architectural,
  aerial_dunes:       VERIFIED.atmospheric,
  aerial_roads:       VERIFIED.topology_dark,
  earth_night:        VERIFIED.earth_curve,
  power_grid_night:   VERIFIED.topology_dark,
  wind_at_dusk:       VERIFIED.atmospheric,
  refinery:           VERIFIED.architectural,
  brutalist:          VERIFIED.architectural,
  concrete_void:      VERIFIED.architectural,
  monumental_arch:    VERIFIED.architectural,
  fiber_light:        VERIFIED.topology_dark,
  dark_waves:         VERIFIED.fluid_dark,
  topo_lines:         VERIFIED.topology_dark,
  mist_valley:        VERIFIED.atmospheric,

  // Direct base aliases
  pastel_arch_orange:  VERIFIED.ridge_dark,
  pastel_arch_violet:  VERIFIED.forest_ridge,
  pastel_arch_cream:   VERIFIED.architectural,
  pastel_arch_peach:   VERIFIED.atmospheric,
  white_curve:         VERIFIED.prism_dark,
  abstract_pastel:     VERIFIED.fluid_dark,
  abstract_waves:      VERIFIED.fluid_dark,
  abstract_topology:   VERIFIED.topology_dark,
  abstract_silk:       VERIFIED.fluid_dark,
  fluid_macro:         VERIFIED.fluid_dark,
  paper_cream:         VERIFIED.architectural,
  paper_texture:       VERIFIED.architectural,
  paper_minimal:       VERIFIED.prism_dark,
  lab_glassware:       VERIFIED.fluid_dark,
  lab_vial:            VERIFIED.fluid_dark,
  lab_macro:           VERIFIED.fluid_dark,
  microscope_soft:     VERIFIED.fluid_dark,
  light_prism:         VERIFIED.prism_dark,
  light_through_glass: VERIFIED.prism_dark,
  render_pastel_geo:   VERIFIED.topology_dark,
  render_topology:     VERIFIED.topology_dark,
  render_3d_sphere:    VERIFIED.cosmic_dust,
  render_3d_geo:       VERIFIED.topology_dark,
  joel_orange:         VERIFIED.ridge_dark,
  joel_violet:         VERIFIED.forest_ridge,
  joel_peach:          VERIFIED.atmospheric,
  abstract_wave:       VERIFIED.fluid_dark,
  abstract_topo:       VERIFIED.topology_dark,
  lab_soft:            VERIFIED.fluid_dark,
};

import photography from "./photography.json";

// Version the campaign assets independently of existing admin-approved photo overrides.
export const PHOTO_VERSION = "helios-ecosystems-v4";
export const campaignPhoto = name => photography[name] || {};
export const campaignPhotoStyle = name => ({
  "--hero-object-position": photography[name]?.position,
  "--hero-mobile-position": photography[name]?.mobile,
});

// Campaign defaults remain overridable from the existing photo administration portal.
export const CAMPAIGN_SLOTS = {
  home: "home_hero", product: "product_hero", helios: "helios_hero", brain: "brain_hero", factory: "factory_hero",
  experience: "experience_hero", research: "research_hero", careers: "careers_hero", framework: "framework_hero",
  company: "company_hero", about: "about_hero", trust: "trust_hero", europe: "europe_hero", industries: "industries_hero",
  partners: "partners_hero", "system-integrators": "system_integrators_hero", embedded: "embedded_hero",
  banking: "industry_banking", "sovereign-wealth": "industry_sovereign", "real-estate": "industry_real_estate",
  insurance: "industry_insurance", healthcare: "industry_healthcare", energy: "industry_energy",
  manufacturing: "industry_manufacturing", "public-sector": "industry_public_sector", telecommunications: "industry_telecom",
  retail: "industry_retail", maritime: "industry_maritime", defense: "industry_defense", space: "industry_space", robotics: "industry_robotics", physical: "industry_physical"
};
Object.entries(CAMPAIGN_SLOTS).forEach(([name, slot]) => { HELIOS_IMG[slot] = `/campaign/${name}.webp`; });
export function campaignImage(name, thumbnail = false) {
  const image = HELIOS_IMG[CAMPAIGN_SLOTS[name]];
  if (image === `/campaign/${name}.webp`) return `/campaign/${name}${thumbnail === "mobile" ? "-mobile" : thumbnail ? "-small" : ""}.webp?v=${photography[name]?.version || PHOTO_VERSION}`;
  return image;
}

export default HELIOS_IMG;

/* ─────────────────────────────────────────────────────────────
   Runtime override system. Hydrates HELIOS_IMG slots from
   approved Nano-Banana photos stored in /api/photos/active.
   ───────────────────────────────────────────────────────────── */
import { API } from "./brand";

let _hydrated = false;
const _listeners = new Set();

export function onImagesHydrated(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

export async function hydrateImageOverrides() {
  if (_hydrated) return;
  _hydrated = true;
  try {
    const r = await fetch(`${API}/photos/active`);
    if (!r.ok) return;
    const photos = await r.json();
    for (const p of photos) {
      if (p.slot && Object.prototype.hasOwnProperty.call(HELIOS_IMG, p.slot)) {
        HELIOS_IMG[p.slot] = `${API}/photos/${p.id}/image`;
      }
    }
    _listeners.forEach((cb) => { try { cb(); } catch (e) { /* ignore listener errors */ } });
  } catch (e) {
    // silent — fall back to defaults
  }
}
